import type {
  BootOptions,
  EndUser,
  EngagementSDK,
} from '@amplitude/engagement-browser';
import { init as initEngagementSdk } from '@amplitude/engagement-browser';

import { trackGuidesAndSurveysEvent } from './analytics-base';
import { getEngagementProxyUrls } from './constants';

export interface EngagementBootUser {
  userId: string;
  deviceId?: string;
  userProperties?: EndUser['user_properties'];
}

let engagementInitialized = false;
let engagementBooted = false;
let engagementBootPromise: Promise<void> | undefined;
let engagementLifecycleVersion = 0;

const getEngagementInstance = () =>
  window.engagement as EngagementSDK | undefined;

/**
 * Initializes the standalone Amplitude engagement SDK with first-party proxy URLs.
 * Must be called before bootEngagement.
 */
export function initEngagement(apiKey?: string) {
  if (!apiKey || engagementInitialized) {
    return;
  }

  const { serverUrl, cdnUrl, mediaUrl } = getEngagementProxyUrls();

  initEngagementSdk(apiKey, {
    serverZone: 'US',
    serverUrl,
    cdnUrl,
    mediaUrl,
    integrations: [
      {
        track: (event) => {
          trackGuidesAndSurveysEvent(event.event_type, {
            ...event.event_properties,
          });
        },
      },
    ],
  });

  engagementInitialized = true;
}

/**
 * Boots the engagement SDK for the authenticated user.
 */
export async function bootEngagement(user: EngagementBootUser) {
  const engagement = getEngagementInstance();

  if (!engagementInitialized || engagementBooted || !engagement) {
    return;
  }

  if (engagementBootPromise) {
    return engagementBootPromise;
  }

  const bootOptions: BootOptions = {
    user: {
      user_id: user.userId,
      device_id: user.deviceId,
      user_properties: user.userProperties,
    },
  };
  const lifecycleVersion = engagementLifecycleVersion;

  const bootPromise = engagement
    .boot(bootOptions)
    .then(() => {
      if (engagementLifecycleVersion === lifecycleVersion) {
        engagementBooted = true;
      }
    })
    .finally(() => {
      if (engagementBootPromise === bootPromise) {
        engagementBootPromise = undefined;
      }
    });

  engagementBootPromise = bootPromise;

  return engagementBootPromise;
}

/**
 * Shuts down the engagement SDK, for example on logout.
 */
export function shutdownEngagement() {
  const engagement = getEngagementInstance();

  if (!engagementInitialized || !engagement) {
    return;
  }

  engagementLifecycleVersion += 1;
  engagementBootPromise = undefined;

  try {
    engagement.shutdown();
  } catch {
    // Engagement teardown must never block application logout.
  } finally {
    engagementBooted = false;
  }
}

export function isEngagementInitialized() {
  return engagementInitialized;
}

export function isEngagementBooted() {
  return engagementBooted;
}

/**
 * Resets module state. Intended for unit tests.
 */
export function resetEngagementState() {
  engagementInitialized = false;
  engagementBooted = false;
  engagementBootPromise = undefined;
  engagementLifecycleVersion = 0;
}
